<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-10">

        <!-- Page Header -->
        <header class="tw-mb-8">
          <h2 class="page-title">Finance</h2>
          <p class="page-subtitle">View your commissions, statements, and payment history.</p>
        </header>

        <!-- Summary Cards Row -->
        <div class="summary-grid tw-mb-8">
          <div class="summary-card summary-card--blue" @click="openCommissions('paid')">
            <div class="summary-card__icon">
              <v-icon color="#00AEEF" size="28">mdi-cash-multiple</v-icon>
            </div>
            <div class="summary-card__body">
              <div class="summary-card__label">Paid Commissions (YTD)</div>
              <div class="summary-card__value">{{ formatCurrency(summary.earnedCommissions) }}</div>
            </div>
            <v-icon color="#E2E8F0" class="tw-ml-auto">mdi-chevron-right</v-icon>
          </div>

          <div class="summary-card summary-card--green" @click="openCommissions('receipted')">
            <div class="summary-card__icon">
              <v-icon color="#10b981" size="28">mdi-check-circle-outline</v-icon>
            </div>
            <div class="summary-card__body">
              <div class="summary-card__label">Commissions on Receipted</div>
              <div class="summary-card__value">{{ formatCurrency(summary.earnedCommissions) }}</div>
            </div>
            <v-icon color="#E2E8F0" class="tw-ml-auto">mdi-chevron-right</v-icon>
          </div>

          <div class="summary-card summary-card--yellow" @click="openCommissions('payable')">
            <div class="summary-card__icon">
              <v-icon color="#F59E0B" size="28">mdi-clock-outline</v-icon>
            </div>
            <div class="summary-card__body">
              <div class="summary-card__label">Payable</div>
              <div class="summary-card__value">{{ formatCurrency(summary.commissionsPayable) }}</div>
            </div>
            <v-icon color="#E2E8F0" class="tw-ml-auto">mdi-chevron-right</v-icon>
          </div>
        </div>

        <!-- Dashboard Content -->
        <div v-if="loading" class="finance-loading">
          <!-- Breakdown Tiles Shimmer -->
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 tw-mb-8">
            <div
              v-for="n in 3"
              :key="`finance-breakdown-shimmer-${n}`"
              class="finance-shimmer-breakdown"
            >
              <div class="shimmer shimmer-breakdown-icon"></div>
              <div class="shimmer shimmer-breakdown-title"></div>
              <div class="shimmer shimmer-breakdown-desc"></div>
              <div class="shimmer shimmer-breakdown-desc shimmer-breakdown-desc-short"></div>
              <div class="shimmer shimmer-breakdown-action"></div>
            </div>
          </div>

          <!-- Recently Closed Sales Shimmer -->
          <div class="activity-card finance-shimmer-card">
            <div class="activity-header">
              <div class="shimmer shimmer-activity-title"></div>

              <div class="tw-flex tw-gap-2">
                <div class="shimmer shimmer-stat-badge"></div>
                <div class="shimmer shimmer-stat-badge shimmer-stat-badge-wide"></div>
              </div>
            </div>

            <div class="finance-shimmer-table">
              <div class="finance-shimmer-table-header">
                <div class="shimmer shimmer-table-header-client"></div>
                <div class="shimmer shimmer-table-header-product"></div>
                <div class="shimmer shimmer-table-header-date"></div>
                <div class="shimmer shimmer-table-header-premium"></div>
                <div class="shimmer shimmer-table-header-docs"></div>
              </div>

              <div
                v-for="n in 5"
                :key="`finance-table-shimmer-${n}`"
                class="finance-shimmer-table-row"
              >
                <div>
                  <div class="shimmer shimmer-table-client"></div>
                  <div class="shimmer shimmer-table-policy"></div>
                </div>

                <div class="shimmer shimmer-table-product"></div>

                <div class="shimmer shimmer-table-date"></div>

                <div class="shimmer shimmer-table-premium"></div>

                <div class="shimmer shimmer-table-docs"></div>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <!-- Breakdown Tiles -->
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 tw-mb-8">
            <div class="breakdown-tile" @click="openCommissions('earned_vs_paid')">
              <div class="breakdown-tile__icon tw-bg-blue-50">
                <v-icon color="#00AEEF">mdi-file-percent-outline</v-icon>
              </div>
              <div class="breakdown-tile__title">Earned vs Paid</div>
              <div class="breakdown-tile__desc">View detailed commission breakdowns and payment history.</div>
              <div class="breakdown-tile__action">View Details <v-icon x-small color="inherit">mdi-arrow-right</v-icon></div>
            </div>

            <div class="breakdown-tile" @click="setActiveTab('sales')">
              <div class="breakdown-tile__icon tw-bg-indigo-50">
                <v-icon color="#4F46E5">mdi-shield-check-outline</v-icon>
              </div>
              <div class="breakdown-tile__title">Closed Sales</div>
              <div class="breakdown-tile__desc">Track your successfully converted policies and activity.</div>
              <div class="breakdown-tile__action">View List <v-icon x-small color="inherit">mdi-arrow-right</v-icon></div>
            </div>

            <div class="breakdown-tile" @click="setActiveTab('documents')">
              <div class="breakdown-tile__icon tw-bg-teal-50">
                <v-icon color="#0D9488">mdi-folder-open-outline</v-icon>
              </div>
              <div class="breakdown-tile__title">Policy Activity</div>
              <div class="breakdown-tile__desc">Access policy schedules, certificates, and valuation reports.</div>
              <div class="breakdown-tile__action">Access Files <v-icon x-small color="inherit">mdi-arrow-right</v-icon></div>
            </div>
          </div>

          <!-- Active View (Sales Activity) -->
          <div v-if="activeTab === 'sales'" class="activity-card">
            <div class="activity-header">
              <h3 class="activity-title">Recently Closed Sales</h3>
              <div class="tw-flex tw-gap-2">
                <div class="stat-badge">
                  <span class="stat-badge__label">MTD:</span>
                  <span class="stat-badge__value">{{ summary.closedSales.countMtd }}</span>
                </div>
                <div class="stat-badge">
                  <span class="stat-badge__label">Value:</span>
                  <span class="stat-badge__value">{{ formatCurrency(summary.closedSales.valueMtd) }}</span>
                </div>
              </div>
            </div>

            <div class="table-container">
              <table v-if="recentConversions.length > 0" class="activity-table">
                <thead>
                  <tr>
                    <th>Client / Policy</th>
                    <th>Product</th>
                    <th>Effective Date</th>
                    <th class="tw-text-right">Premium</th>
                    <th class="tw-text-center">Docs</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="conv in recentConversions" :key="conv.policyNo">
                    <td>
                      <div class="tw-font-bold tw-text-[#1E4276]">{{ conv.clientName }}</div>
                      <div class="tw-text-xs tw-text-gray-400 tw-font-mono">{{ conv.policyNo }}</div>
                    </td>
                    <td><span class="product-badge">{{ conv.product }}</span></td>
                    <td class="tw-text-gray-500 tw-text-sm">{{ conv.coverFrom }}</td>
                    <td class="tw-text-right tw-font-bold">{{ formatCurrency(conv.premium) }}</td>
                    <td class="tw-text-center">
                      <div class="tw-flex tw-justify-center tw-gap-1">
                        <v-btn icon x-small color="#00AEEF" title="View Schedule" @click="viewDocs(conv)">
                          <v-icon small>mdi-file-document-outline</v-icon>
                        </v-btn>
                        <v-btn icon x-small color="#F59E0B" title="Valuation Report" @click="viewValuation(conv)">
                          <v-icon small>mdi-file-search-outline</v-icon>
                        </v-btn>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="empty-activity">
                <v-icon size="40" color="#E2E8F0">mdi-text-box-search-outline</v-icon>
                <p>No recent sales found</p>
              </div>
            </div>
          </div>

          <!-- Documents View -->
          <div v-if="activeTab === 'documents'" class="activity-card">
             <div class="activity-header">
              <h3 class="activity-title">Policy Documents Repository</h3>
              <div class="tw-relative">
                <v-text-field
                  v-model="docFilter"
                  placeholder="Policy or Client..."
                  outlined dense hide-details
                  class="search-mini"
                  prepend-inner-icon="mdi-magnify"
                ></v-text-field>
              </div>
            </div>
            <div class="table-container">
              <table v-if="filteredDocuments.length > 0" class="activity-table">
                <thead>
                  <tr>
                    <th>Policy / Client</th>
                    <th>Product</th>
                    <th>Ref</th>
                    <th class="tw-text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="doc in filteredDocuments" :key="doc.policyNo">
                    <td>
                      <div class="tw-font-bold tw-text-[#1E4276]">{{ doc.policyNo }}</div>
                      <div class="tw-text-xs tw-text-gray-400">{{ doc.clientName }}</div>
                    </td>
                    <td><span class="product-badge">{{ doc.product }}</span></td>
                    <td class="tw-text-xs tw-text-gray-500 tw-font-mono">{{ doc.batchNo || '—' }}</td>
                    <td class="tw-text-center">
                      <div class="tw-flex tw-justify-center tw-gap-1">
                        <v-btn icon x-small color="#00AEEF" title="View Schedule" @click="viewDocs(doc)">
                          <v-icon small>mdi-file-eye-outline</v-icon>
                        </v-btn>
                        <v-btn icon x-small color="#10b981" title="Download Cert" @click="downloadCert(doc)">
                          <v-icon small>mdi-certificate-outline</v-icon>
                        </v-btn>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="empty-activity">
                <v-icon size="40" color="#E2E8F0">mdi-folder-search-outline</v-icon>
                <p>No matching documents found</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </agent-default>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import axios from 'axios';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'AgentFinance',
  components: { AgentDefault },
  data() {
    return {
      loading: true,
      activeTab: 'sales',
      summary: {
        earnedCommissions: 0,
        commissionsPayable: 0,
        closedSales: { countMtd: 0, valueMtd: 0 },
      },
      recentConversions: [],
      docFilter: '',
    };
  },
  computed: {
    filteredDocuments() {
      if (!this.docFilter) return this.recentConversions;
      const q = this.docFilter.toLowerCase();
      return this.recentConversions.filter((d) => (
        d.policyNo.toLowerCase().includes(q) || d.clientName.toLowerCase().includes(q)
      ));
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    openCommissions(source) {
      trackAgentInteraction(this, 'finance_open_commissions', { source });
      this.$router.push('/agent/commissions');
    },
    setActiveTab(tab) {
      this.activeTab = tab;
      trackAgentInteraction(this, 'finance_switch_tab', { tab });
    },
    formatCurrency(value) {
      if (value == null || value === '') return 'Ksh 0';
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(value);
    },
    async fetchData() {
      this.loading = true;
      try {
        const user = this.$store?.getters?.['auth/authenticatedUser'];
        const email = user?.email || '';
        const agnCode = user?.['custom:agent_code'];

        const params = agnCode ? { agn_code: agnCode } : { email };

        const [summaryRes, convRes] = await Promise.all([
          axios.get('/customer/gis/agent/dashboard/summary', { params, validateStatus: () => true }),
          axios.get('/customer/gis/agent/conversions', { params: { ...params, limit: 10 }, validateStatus: () => true }),
        ]);

        if (summaryRes.status === 200 && summaryRes.data.summary) {
          this.summary = summaryRes.data.summary;
        }

        if (convRes.status === 200) {
          // ResponseBuilder wraps in .object
          const rawDoc = convRes.data.object || convRes.data;
          this.recentConversions = rawDoc.conversions || [];
        }
      } catch (err) {
        console.error('Failed to fetch finance data', err);
      } finally {
        this.loading = false;
      }
    },
    viewDocs(conv) {
      trackAgentInteraction(this, 'finance_view_docs', {
        policy_number: conv?.policyNo || 'unknown',
      });
      if (conv.batchNo) {
        this.$notify.info(`Generating schedule for ${conv.policyNo}...`);
      }
    },
    downloadCert(conv) {
      trackAgentInteraction(this, 'finance_download_certificate', {
        policy_number: conv?.policyNo || 'unknown',
      });
      this.$notify.info(`Fetching digital certificate for ${conv.policyNo}...`);
    },
    viewValuation(conv) {
      trackAgentInteraction(this, 'finance_view_valuation', {
        policy_number: conv?.policyNo || 'unknown',
      });
      this.$notify.info(`Fetching valuation report for ${conv.policyNo}...`);
    },
  },
  metaInfo: { title: 'Finance' },
};
</script>

<style scoped>
/* ── Header ── */
.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

/* ── Summary Grid ── */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.summary-card {
  background: white;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
}

.summary-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.09);
}

.summary-card__icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-card--blue .summary-card__icon { background: #EBF8FF; }
.summary-card--green .summary-card__icon { background: #ECFDF5; }
.summary-card--yellow .summary-card__icon { background: #FFFBEB; }

.summary-card__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.summary-card__value {
  font-size: 1.4rem;
  font-weight: 800;
  color: #1E4276;
}

/* ── Breakdown Tiles ── */
.breakdown-tile {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 16px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.breakdown-tile:hover {
  border-color: #00AEEF;
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 174, 239, 0.08);
}

.breakdown-tile__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.breakdown-tile__title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #1E4276;
}

.breakdown-tile__desc {
  font-size: 0.82rem;
  color: #94A3B8;
  line-height: 1.6;
}

.breakdown-tile__action {
  margin-top: auto;
  font-size: 0.85rem;
  font-weight: 700;
  color: #00AEEF;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ── Activity Card ── */
.activity-card {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.04);
}

.activity-header {
  padding: 20px 24px;
  border-bottom: 1px solid #F1F5F9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #F8FAFC;
}

.activity-title {
  font-size: 1.1rem;
  font-weight: 800;
  color: #1E4276;
}

.stat-badge {
  background: white;
  border: 1px solid #E2E8F0;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  display: flex;
  gap: 4px;
}

.stat-badge__label { color: #94A3B8; font-weight: 600; }
.stat-badge__value { color: #1E4276; font-weight: 800; }

.table-container {
  overflow-x: auto;
}

.activity-table {
  width: 100%;
  border-collapse: collapse;
}

.activity-table th {
  text-align: left;
  padding: 14px 24px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #F1F5F9;
}

.activity-table td {
  padding: 16px 24px;
  border-bottom: 1px solid #F8FAFC;
}

.product-badge {
  background: #EBF8FF;
  color: #00AEEF;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 6px;
  text-transform: uppercase;
}

.empty-activity {
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #94A3B8;
  font-size: 0.9rem;
}

/* ── Content Card ── */
.content-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.content-card__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 72px 32px;
  text-align: center;
}

.coming-soon-icon {
  width: 88px;
  height: 88px;
  border-radius: 24px;
  background: #EBF8FF;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.coming-soon-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 12px 0;
}

.coming-soon-text {
  font-size: 0.925rem;
  color: #64748B;
  max-width: 420px;
  line-height: 1.7;
  margin: 0 0 28px 0;
}

.coming-soon-pill {
  background: #EBF8FF;
  color: #00AEEF;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 6px 18px;
  border-radius: 20px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
/* ── Finance Shimmer Loading ── */

.finance-loading {
  width: 100%;
}

.shimmer {
  position: relative;
  overflow: hidden;
  background: #E9EEF3;
  border-radius: 6px;
}

.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.65) 50%,
    transparent 100%
  );
  animation: finance-shimmer 1.5s infinite;
}

@keyframes finance-shimmer {
  0% {
    left: -150%;
  }

  100% {
    left: 150%;
  }
}

/* ── Breakdown Tiles ── */

.finance-shimmer-breakdown {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 16px;
  padding: 24px;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.shimmer-breakdown-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
}

.shimmer-breakdown-title {
  width: 130px;
  height: 18px;
  margin-top: 4px;
}

.shimmer-breakdown-desc {
  width: 90%;
  height: 13px;
}

.shimmer-breakdown-desc-short {
  width: 65%;
}

.shimmer-breakdown-action {
  width: 95px;
  height: 14px;
  margin-top: auto;
}

/* ── Activity Card ── */

.finance-shimmer-card {
  overflow: hidden;
}

.shimmer-activity-title {
  width: 190px;
  height: 18px;
}

.shimmer-stat-badge {
  width: 70px;
  height: 28px;
  border-radius: 20px;
}

.shimmer-stat-badge-wide {
  width: 105px;
}

/* ── Finance Table ── */

.finance-shimmer-table {
  width: 100%;
  overflow-x: auto;
}

.finance-shimmer-table-header,
.finance-shimmer-table-row {
  display: grid;
  grid-template-columns:
    minmax(180px, 1.5fr)
    minmax(130px, 1fr)
    minmax(130px, 1fr)
    minmax(120px, 1fr)
    minmax(80px, 0.7fr);
  align-items: center;
  column-gap: 20px;
  min-width: 700px;
  padding-left: 24px;
  padding-right: 24px;
}

.finance-shimmer-table-header {
  min-height: 48px;
  background: #F8FAFC;
  border-bottom: 2px solid #F1F5F9;
}

.finance-shimmer-table-row {
  min-height: 72px;
  border-bottom: 1px solid #F8FAFC;
}

/* Table header placeholders */

.shimmer-table-header-client {
  width: 105px;
  height: 11px;
}

.shimmer-table-header-product {
  width: 55px;
  height: 11px;
}

.shimmer-table-header-date {
  width: 90px;
  height: 11px;
}

.shimmer-table-header-premium {
  width: 55px;
  height: 11px;
  margin-left: auto;
}

.shimmer-table-header-docs {
  width: 35px;
  height: 11px;
  margin: auto;
}

/* Table row placeholders */

.shimmer-table-client {
  width: 125px;
  height: 15px;
  margin-bottom: 7px;
}

.shimmer-table-policy {
  width: 90px;
  height: 11px;
}

.shimmer-table-product {
  width: 80px;
  height: 23px;
  border-radius: 6px;
}

.shimmer-table-date {
  width: 90px;
  height: 14px;
}

.shimmer-table-premium {
  width: 85px;
  height: 15px;
  margin-left: auto;
}

.shimmer-table-docs {
  width: 54px;
  height: 30px;
  border-radius: 8px;
  margin: auto;
}
</style>
